import {
	asArray,
	asObject,
	asString,
	describePayload,
	LocationLines,
	WhatsAppBubble,
	WhatsAppPreview,
} from "@commerce/channels/whatsapp-preview/WhatsAppPreview";
import { Badge } from "@ui/components/badge";
import { cn } from "@ui/lib";
import {
	ContactRoundIcon,
	CornerUpLeftIcon,
	FileTextIcon,
	ImageIcon,
	MegaphoneIcon,
	ShoppingBagIcon,
	ShoppingCartIcon,
} from "lucide-react";
import type { ReactNode } from "react";
import { DeliveryTicks } from "./DeliveryTicks";
import type { ChannelMessageRow } from "./types";
import { VoiceNote } from "./VoiceNote";
import { WhatsAppText } from "./WhatsAppText";

/** Our org-member route for a stored photo or voice note (private, never cached). */
export const channelMediaUrl = (row: Pick<ChannelMessageRow, "id">) =>
	`/api/channel-media/${encodeURIComponent(row.id)}`;

/**
 * One WhatsApp message as it was sent or received (`ChannelMessage`). Outbound messages are
 * drawn from the exact payload we sent, inbound ones from the normalised webhook content.
 * `readAs` is how the agent read a photo or what a voice note said (the turn's text);
 * `tappedId` marks the button or row the shopper chose on an outbound message.
 */
export function ChannelMessageView({
	row,
	readAs,
	tappedId,
	showTicks = true,
}: {
	row: ChannelMessageRow;
	readAs?: string | null;
	tappedId?: string | null;
	showTicks?: boolean;
}) {
	if (row.direction === "OUT") {
		return (
			<div className="flex min-w-0 flex-col items-start gap-1">
				<WhatsAppPreview
					kind={row.kind}
					payload={row.payload}
					mediaUrl={row.mediaKey ? channelMediaUrl(row) : null}
					tappedId={tappedId}
				/>
				{showTicks ? <DeliveryTicks row={row} /> : null}
			</div>
		);
	}
	return <InboundView row={row} readAs={readAs ?? null} />;
}

function InboundView({
	row,
	readAs,
}: {
	row: ChannelMessageRow;
	readAs: string | null;
}) {
	const payload = asObject(row.payload);
	const content = asObject(payload.content);
	const referral = asObject(payload.referral);
	const referred = asObject(payload.referredProduct);
	const context = (
		<>
			{asString(referral.headline) || asString(referral.sourceId) ? (
				<ContextLine icon={MegaphoneIcon}>
					From an ad
					{asString(referral.headline)
						? `: ${String(referral.headline)}`
						: ""}
				</ContextLine>
			) : null}
			{asString(referred.retailerId) ? (
				<ContextLine icon={ShoppingBagIcon}>
					About{" "}
					<span className="font-mono">
						{String(referred.retailerId)}
					</span>
				</ContextLine>
			) : null}
			{row.replyToWamid && content.type !== "reply" ? (
				<ContextLine icon={CornerUpLeftIcon}>
					Replying to a message
				</ContextLine>
			) : null}
			{payload.forwarded ? (
				<ContextLine icon={CornerUpLeftIcon}>Forwarded</ContextLine>
			) : null}
		</>
	);

	return (
		<div className="flex min-w-0 flex-col items-end gap-1">
			{context}
			<InboundContent row={row} content={content} readAs={readAs} />
		</div>
	);
}

function InboundContent({
	row,
	content,
	readAs,
}: {
	row: ChannelMessageRow;
	content: Record<string, unknown>;
	readAs: string | null;
}) {
	switch (content.type) {
		case "text":
			return (
				<WhatsAppBubble align="end">
					<WhatsAppText text={asString(content.text) ?? ""} />
				</WhatsAppBubble>
			);
		case "reply":
			return (
				<Badge variant="outline" className="max-w-full">
					<CornerUpLeftIcon aria-hidden />
					<span className="truncate" dir="auto">
						Tapped {asString(content.text) ?? "a button"}
					</span>
				</Badge>
			);
		case "image":
			return <InboundImage row={row} content={content} readAs={readAs} />;
		case "audio":
			return <InboundAudio row={row} content={content} readAs={readAs} />;
		case "location":
			return (
				<WhatsAppBubble align="end">
					<LocationLines
						name={asString(content.name)}
						address={asString(content.address)}
						latitude={content.latitude}
						longitude={content.longitude}
					/>
				</WhatsAppBubble>
			);
		case "order":
			return <InboundOrder content={content} />;
		case "flow":
			return <FlowAnswers response={asObject(content.response)} />;
		case "product":
			return <InboundProduct content={content} />;
		case "contacts":
			return <InboundContacts content={content} />;
		case "reaction": {
			const emoji = asString(content.emoji);
			return (
				<span className="text-muted-foreground text-xs">
					{emoji ? `Reacted ${emoji}` : "Removed a reaction"}
				</span>
			);
		}
		default:
			return (
				<WhatsAppBubble align="end">
					<p className="text-muted-foreground text-xs">
						{asString(content.kind)
							? `Sent a ${String(content.kind)}`
							: `Sent ${describePayload(row.kind, row.payload).toLowerCase()}`}
					</p>
				</WhatsAppBubble>
			);
	}
}

interface InboundProps {
	row: ChannelMessageRow;
	content: Record<string, unknown>;
	readAs: string | null;
}

/** A photo the shopper sent (only through the media route), its caption and how it was read. */
function InboundImage({ row, content, readAs }: InboundProps) {
	return (
		<WhatsAppBubble align="end">
			{row.mediaKey ? (
				<a
					href={channelMediaUrl(row)}
					target="_blank"
					rel="noreferrer"
					className="-mx-2 -mt-1 block"
				>
					<img
						src={channelMediaUrl(row)}
						alt="Sent by the shopper"
						loading="lazy"
						className="max-h-72 w-full rounded-xl bg-muted object-cover"
					/>
				</a>
			) : (
				<span className="flex items-center gap-2 text-muted-foreground text-xs">
					<ImageIcon className="size-4" aria-hidden />
					Photo not stored
				</span>
			)}
			{asString(content.caption) ? (
				<WhatsAppText text={String(content.caption)} />
			) : null}
			{readAs ? (
				<p className="text-muted-foreground text-xs" dir="auto">
					Read as: {readAs}
				</p>
			) : null}
		</WhatsAppBubble>
	);
}

/** A voice note or audio file, played through the media route, with its transcript. */
function InboundAudio({ row, content, readAs }: InboundProps) {
	return (
		<WhatsAppBubble align="end">
			{row.mediaKey ? (
				<VoiceNote src={channelMediaUrl(row)} transcript={readAs} />
			) : (
				<p className="text-muted-foreground text-xs">
					{content.voice ? "Voice note" : "Audio"} not stored
				</p>
			)}
			{readAs ? (
				<p className="text-pretty text-sm" dir="auto">
					{readAs}
				</p>
			) : null}
		</WhatsAppBubble>
	);
}

/** A cart sent from the catalog: its size and total, merged into the basket. */
function InboundOrder({ content }: { content: Record<string, unknown> }) {
	const items = asArray(content.items).map(asObject);
	const count = items.reduce(
		(sum, item) => sum + (Number(item.quantity) || 0),
		0,
	);
	const total = items.reduce(
		(sum, item) =>
			sum + (Number(item.price) || 0) * (Number(item.quantity) || 0),
		0,
	);
	const currency = asString(items[0]?.currency);
	return (
		<WhatsAppBubble align="end">
			<p className="flex items-center gap-1.5 font-medium">
				<ShoppingCartIcon className="size-4" aria-hidden />
				Cart · {count} {count === 1 ? "item" : "items"}
				{total > 0 && currency
					? ` · ${currency} ${total.toFixed(2)}`
					: ""}
			</p>
			{asString(content.text) ? (
				<WhatsAppText text={String(content.text)} />
			) : null}
			<p className="text-muted-foreground text-xs">Merged into basket</p>
		</WhatsAppBubble>
	);
}

/** A question about one catalog product. */
function InboundProduct({ content }: { content: Record<string, unknown> }) {
	return (
		<WhatsAppBubble align="end">
			<p className="flex items-center gap-1.5">
				<ShoppingBagIcon className="size-4 shrink-0" aria-hidden />
				<span dir="auto" className="font-medium">
					{asString(content.title) ?? "A product"}
				</span>
			</p>
			{content.price != null && asString(content.currency) ? (
				<p className="text-muted-foreground text-xs tabular">
					{String(content.currency)}{" "}
					{Number(content.price).toFixed(2)}
				</p>
			) : null}
		</WhatsAppBubble>
	);
}

/** A shared contact, or the shopper's own number when we asked for it. */
function InboundContacts({ content }: { content: Record<string, unknown> }) {
	const phones = asArray(content.phones).flatMap(
		(phone) => asString(phone) ?? [],
	);
	return (
		<WhatsAppBubble align="end">
			<p className="flex items-center gap-1.5">
				<ContactRoundIcon className="size-4" aria-hidden />
				{content.origin === "contact_request"
					? "Shared their phone number"
					: "Shared a contact"}
			</p>
			{phones.length ? (
				<p className="font-mono text-muted-foreground text-xs">
					{phones.join(", ")}
				</p>
			) : null}
		</WhatsAppBubble>
	);
}

/** A submitted checkout form: each answer as a label and a value, tokens left out. */
function FlowAnswers({ response }: { response: Record<string, unknown> }) {
	const answers = Object.entries(response).filter(
		([key, value]) =>
			key !== "flow_token" && value !== null && value !== undefined,
	);
	return (
		<WhatsAppBubble align="end">
			<p className="flex items-center gap-1.5 font-medium">
				<FileTextIcon className="size-4" aria-hidden />
				Form answers
			</p>
			{answers.length ? (
				<dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-0.5 text-xs">
					{answers.map(([key, value]) => (
						<div key={key} className="contents">
							<dt className="text-muted-foreground">
								{labelOf(key)}
							</dt>
							<dd dir="auto" className="break-words">
								{typeof value === "object"
									? JSON.stringify(value)
									: String(value)}
							</dd>
						</div>
					))}
				</dl>
			) : (
				<p className="text-muted-foreground text-xs">No answers</p>
			)}
		</WhatsAppBubble>
	);
}

/** "delivery_slot" → "Delivery slot". */
const labelOf = (key: string) =>
	key.replaceAll("_", " ").replace(/^./, (c) => c.toUpperCase());

function ContextLine({
	icon: Icon,
	children,
	className,
}: {
	icon: typeof MegaphoneIcon;
	children: ReactNode;
	className?: string;
}) {
	return (
		<span
			className={cn(
				"flex items-center gap-1 text-muted-foreground text-xs",
				className,
			)}
		>
			<Icon className="size-3" aria-hidden />
			<span dir="auto" className="truncate">
				{children}
			</span>
		</span>
	);
}
